import {
  createCalendarEvent, isCalendarEvent, isDateKey, isTimeKey, timeToMinutes, updateCalendarEvent,
} from "./plannerItems";

const CONTEXT_PAST_DAYS = 7;
const CONTEXT_FUTURE_DAYS = 120;
const CONTEXT_MAX_EVENTS = 60;
const LAST_MINUTE = 23 * 60 + 59;

export const isGhostEvent = (event) => isCalendarEvent(event) && event.status === "pending";

const pad = (n) => String(n).padStart(2, "0");
const minutesToTime = (mins) => `${pad(Math.floor(mins / 60))}:${pad(mins % 60)}`;
const parseKey = (key) => {
  const [y, m, d] = key.split("-").map(Number);
  return new Date(y, m - 1, d);
};
const shiftKey = (key, days) => {
  const date = parseKey(key);
  date.setDate(date.getDate() + days);
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
};

export function shortTime(time) {
  const [h, m] = time.split(":").map(Number);
  const suffix = h < 12 ? "am" : "pm";
  const hour = h % 12 === 0 ? 12 : h % 12;
  return m === 0 ? `${hour}${suffix}` : `${hour}:${pad(m)}${suffix}`;
}

export function describeEventWhen(event) {
  const day = parseKey(event.date).toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" });
  return `${day}, ${shortTime(event.start_time)}–${shortTime(event.end_time)}`;
}

export function describeToday(now = new Date()) {
  const date = `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;
  const weekday = now.toLocaleDateString("en-US", { weekday: "long" });
  return `${weekday} ${date}, ${pad(now.getHours())}:${pad(now.getMinutes())}`;
}

export function describeCalendarForLumen(items, todayKey) {
  const from = shiftKey(todayKey, -CONTEXT_PAST_DAYS);
  const to = shiftKey(todayKey, CONTEXT_FUTURE_DAYS);
  const events = (items || [])
    .filter((e) => isCalendarEvent(e) && e.date >= from && e.date <= to)
    .sort((a, b) => `${a.date}${a.start_time}`.localeCompare(`${b.date}${b.start_time}`))
    .slice(0, CONTEXT_MAX_EVENTS);
  if (!events.length) return "empty";
  return events.map((e) => {
    const weekday = parseKey(e.date).toLocaleDateString("en-US", { weekday: "short" });
    const ghost = isGhostEvent(e) ? " (you suggested this, waiting for the user to confirm)" : "";
    return `[id: ${e.id}] ${e.date} ${weekday} ${e.start_time}-${e.end_time} ${e.title}${ghost}`;
  }).join("\n");
}

const cleanTitle = (value) => String(value ?? "").trim();
const sameEvent = (a, b) =>
  a.date === b.date && a.start_time === b.start_time && a.title.toLowerCase() === b.title.toLowerCase();

const receiptFor = (action, event, extra = {}) => ({
  action,
  eventId: event?.id || null,
  title: event?.title || "",
  date: event?.date || null,
  start_time: event?.start_time || null,
  end_time: event?.end_time || null,
  ...extra,
});

const failed = (reason, update = {}) => ({
  ok: false,
  reason,
  receipt: { action: "failed", reason, title: cleanTitle(update.title || update.content) },
});

function fieldsFrom(update, base = {}) {
  const fields = {};
  const title = cleanTitle(update.title || update.content);
  if (title) fields.title = title;
  if (update.date !== undefined) fields.date = update.date;
  if (update.start_time !== undefined) fields.start_time = update.start_time;
  if (update.end_time !== undefined) fields.end_time = update.end_time;
  const start = fields.start_time ?? base.start_time;
  const end = fields.end_time ?? base.end_time;
  const startMoved = fields.start_time !== undefined && fields.start_time !== base.start_time;
  if (isTimeKey(start) && (!end || (startMoved && fields.end_time === undefined))) {
    const length = base.start_time && base.end_time && isTimeKey(base.start_time) && isTimeKey(base.end_time)
      ? timeToMinutes(base.end_time) - timeToMinutes(base.start_time)
      : 60;
    fields.end_time = minutesToTime(Math.min(timeToMinutes(start) + length, LAST_MINUTE));
  }
  return fields;
}

function whyInvalid(event) {
  if (!event.title) return "missing_title";
  if (!isDateKey(event.date)) return "missing_date";
  if (!isTimeKey(event.start_time)) return "missing_time";
  if (!isTimeKey(event.end_time) || timeToMinutes(event.end_time) <= timeToMinutes(event.start_time)) return "end_before_start";
  return "invalid";
}

export function planLumenCalendarUpdate(update, items) {
  const type = String(update?.type || "event").toLowerCase();
  const events = (items || []).filter(isCalendarEvent);
  const byId = (id) => events.find((e) => e.id === id) || null;

  if (type === "remove") {
    const event = byId(update.target || update.replaces);
    if (!event) return failed("not_found", update);
    return { ok: true, op: "remove", id: event.id, receipt: receiptFor("removed", event) };
  }

  if (type === "confirm") {
    const event = byId(update.target || update.replaces);
    if (!event) return failed("not_found", update);
    if (!isGhostEvent(event)) return { ok: true, op: "none", receipt: receiptFor("exists", event) };
    return { ok: true, op: "confirm", id: event.id, receipt: receiptFor("confirmed", { ...event, status: "confirmed" }) };
  }

  if (type !== "event") return failed("unknown_type", update);

  if (update.replaces) {
    const existing = byId(update.replaces);
    if (!existing) return failed("not_found", update);
    const patch = fieldsFrom(update, existing);
    if (update.proposed === true) patch.status = "pending";
    const next = updateCalendarEvent(existing, patch);
    if (!next) return failed(whyInvalid({ ...existing, ...patch }), update);
    const action = isGhostEvent(next) ? "suggested" : "changed";
    return { ok: true, op: "change", id: existing.id, patch, receipt: receiptFor(action, next) };
  }

  const fields = fieldsFrom(update);
  const status = update.proposed === true ? "pending" : "confirmed";
  const event = createCalendarEvent({ ...fields, status }, "lumen");
  if (!event) return failed(whyInvalid({ title: "", ...fields }), update);
  const twin = events.find((e) => sameEvent(e, event));
  if (twin) return { ok: true, op: "none", receipt: receiptFor(isGhostEvent(twin) ? "suggested" : "exists", twin) };
  return { ok: true, op: "add", event, receipt: receiptFor(status === "pending" ? "suggested" : "added", event) };
}

const REASON_TEXT = {
  not_found: "that event isn't on the calendar any more",
  missing_title: "it needs a name",
  missing_date: "it needs a date",
  missing_time: "it needs a start time",
  end_before_start: "the end time has to be after the start",
  unknown_type: "I didn't understand the change",
  not_sent: "the change didn't come through, so ask again",
  invalid: "the details didn't add up",
};

export function describeCalendarReceipt(receipt) {
  if (!receipt) return "";
  if (receipt.action === "failed") {
    const what = receipt.title ? ` "${receipt.title}"` : "";
    return `Couldn't update your calendar${what}: ${REASON_TEXT[receipt.reason] || REASON_TEXT.invalid}.`;
  }
  const when = receipt.date && receipt.start_time && receipt.end_time ? `, ${describeEventWhen(receipt)}` : "";
  const what = `${receipt.title}${when}`;
  switch (receipt.action) {
    case "added": return `Added to your calendar: ${what}`;
    case "suggested": return `Suggested for your calendar: ${what}. It stays grey until you confirm it.`;
    case "changed": return `Calendar updated: ${what}`;
    case "removed": return `Removed from your calendar: ${what}`;
    case "confirmed": return `Confirmed on your calendar: ${what}`;
    case "exists": return `Already on your calendar: ${what}`;
    default: return "";
  }
}
